import * as React from 'react';
import { Res, ProductForSaleDTO } from 'core/api';
import * as UI from 'core/ui';
import { Result, handleServerErrors } from 'core/infrastructure';
import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import * as Images from 'pos/Images';
import * as SalesStore from '../SalesStore';
import { CatalogueFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Cols, Col } from 'core/ui';
import { Toaster } from 'core/ui-next';
import { Modal } from 'core/ui-next/modal';

type State = { plu: string };

async function getProductByPLU(plu: string): Promise<Result<ProductForSaleDTO>> {
	return await handleServerErrors(async () => {
		return await CatalogueFacade.getSaleableAsMainByPLU(plu, SalesStore.getTicket().Id);
	}, {
		onDomainError: async error => { Toaster.warn(error.message); }
	});
}

class GetPLU extends PosScreenClassComponent<Modal.Actions<ProductForSaleDTO>, State> {

	private form = React.createRef<UI.Form<State>>();
	private txtPLU = React.createRef<UI.TextBox<State>>();

	createInitialState(_props: Modal.Actions<ProductForSaleDTO>): Nullable<State> {
		return { plu: '' };
	}

	onComponentDidMount(): void {
		this.txtPLU.current!.activate();
	}

	onComponentWillUnmount(): void { }

	protected getScreenId(): string {
		return 'SELL_BY_PLU';
	}

	protected getTitle(): string {
		return Res.Web.SELL_BY_PLU();
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getImage(): string | SvgIcon {
		return Images.SellByPLU;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOK });
	}

	private onOK = async () => {
		const valid = await this.form.current!.updateModel();
		if (!valid)
			return;

		const result = await getProductByPLU(this.state.plu);
		if (result.ok)
			this.close(result.value);

		// Si falla no hay que avisar nada porque ya lo hage getProductsByPLU que
		// se reutilizada en la función que hace de punto de entrada de esta operación
	};

	protected getContent(): React.ReactNode {
		const { Form, TextBox } = UI.controlsFor<State>();

		return <Form small ref={this.form} model={this.state}>
			<Cols>
				<Col width='*'>
					<DialogIcon src={Images.SellByPLU} />
				</Col>
			</Cols>
			<Cols>
				<Col width='*' className='m-t-md'>
					<TextBox className='form-control text-center' placeholder={Res.Web.SELL_BY_PLU_PLACEHOLDER()} linkTo={x => x.plu} autoComplete='off' ref={this.txtPLU} required onKeyDown={UI.whenEnterKey(this.onOK)} />
				</Col>
			</Cols>
		</Form>;
	}
}

export default async function getSaleableAsMainByPLU(maybePLU: string): Promise<Result<ProductForSaleDTO>> {
	if (maybePLU)
		return getProductByPLU(maybePLU);

	const result = await Modal.show(GetPLU);
	return result.ok ? Result.ok(result.value) : Result.error();
}


declare global {
	interface CommandType {
		sellByPLU: (maybePLU: string, compose: (product: ProductForSaleDTO, quantity?: number) => Promise<Result>) => Promise<Result>;
	}
}

Command.register('sellByPLU', 'NO_PRECONDITION', async (maybePLU: string, compose: (product: ProductForSaleDTO, quantity?: number) => Promise<Result>): Promise<Result> => {

	const result = maybePLU ? await getProductByPLU(maybePLU) : await Modal.show(GetPLU);
	if (result.ok)
		return await compose(result.value, 1);

	return Result.ok();
});

